import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {LinkifyFormatter} from '../../../../components/common/formatters/markdown/linkifyFormatter';
import {Skeleton} from '../../../../components/common/skeleton';
import {IntegrationTypeModel} from '../../../../models/api/integrationTypesModel';
import {SettingsAppsDetailsUpsellBannerContainer} from './banners/settingsAppsDetailsUpsellBannerContainer';
import {SettingsAppsDetailsImageViewer} from './settingsAppsDetailsImageViewer';

/*
 * Props.
 */

interface SettingsAppsDetailsOverviewPageProps {
  integrationType?: IntegrationTypeModel;
  isLoading?: boolean;
  isIntegrationTypesLoading?: boolean;
}

/*
 * Style.
 */

const StyledOverviewWrapperDiv = styled.div`
  max-width: 600px;
`;

const StyledDescriptionTitleDiv = styled.div`
  margin-top: 24px;
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.veryLarge};
  line-height: 24px;
`;

const StyledDescriptionDiv = styled.div`
  margin-top: 8px;
  line-height: 22px;
  color: ${(p) => p.theme.greys.shade80};
  white-space: pre-wrap;
  font-weight: ${fontWeights.normal};
`;

const StyledSkeletonWrapperDiv = styled.div`
  margin: 14px 0;
`;

/*
 * Component.
 */

export const SettingsAppsDetailsOverviewPage: FC<SettingsAppsDetailsOverviewPageProps> = (props) => {
  const {integrationType, isLoading, isIntegrationTypesLoading} = props;
  return (
    <StyledOverviewWrapperDiv>
      {integrationType && <SettingsAppsDetailsUpsellBannerContainer integrationType={integrationType} />}
      <SettingsAppsDetailsImageViewer integrationType={integrationType} isLoading={Boolean(isLoading)} />
      <StyledDescriptionTitleDiv>{renderTitle(isIntegrationTypesLoading)}</StyledDescriptionTitleDiv>
      {renderDescription(integrationType, isLoading)}
    </StyledOverviewWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderTitle(isIntegrationTypesLoading?: boolean) {
  if (isIntegrationTypesLoading) {
    return <Skeleton height="24px" width="125px" />;
  }
  return <Trans id="settings-apps-details-description-title">Description</Trans>;
}

function renderDescription(integrationType?: IntegrationTypeModel, isLoading?: boolean) {
  if (isLoading || !integrationType || !integrationType.longDescription) {
    return (
      <StyledDescriptionDiv>
        <StyledSkeletonWrapperDiv>
          <Skeleton height="18px" />
          <Skeleton height="18px" />
          <Skeleton height="18px" width="50%" />
        </StyledSkeletonWrapperDiv>
        <StyledSkeletonWrapperDiv>
          <Skeleton height="18px" />
          <Skeleton height="18px" />
          <Skeleton height="18px" width="50%" />
        </StyledSkeletonWrapperDiv>
      </StyledDescriptionDiv>
    );
  }
  return (
    <StyledDescriptionDiv>
      <LinkifyFormatter text={integrationType.longDescription} />
    </StyledDescriptionDiv>
  );
}
